import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

type NavSection = {
    id: string;
    label: string;
};

type NavPage = {
    path: string;
    label: string;
    sections: NavSection[];
};

const NAV_PAGES: NavPage[] = [
    {
        path: '/',
        label: 'Home',
        sections: [
            { id: 'overview', label: 'Overview' },
            { id: 'media-preview', label: 'Media Preview' },
            { id: 'truth-strip', label: 'Truth Classes' },
            { id: 'execution-modes', label: 'Execution Modes' },
            { id: 'controlled-access', label: 'Early Access' },
        ],
    },
    {
        path: '/avenbox',
        label: 'AvenBox',
        sections: [
            { id: 'reserve-track', label: 'Reservation' },
            { id: 'product-definition', label: 'Product Definition' },
            { id: 'use-modes', label: 'Use Modes' },
            { id: 'execution-modes', label: 'Execution Paths' },
            { id: 'truth-state', label: 'Truth State' },
        ],
    },
    {
        path: '/technology',
        label: 'Technology',
        sections: [
            { id: 'overview', label: 'Overview' },
            { id: 'architecture-overview', label: 'Architecture' },
            { id: 'execution-paths', label: 'Execution Paths' },
            { id: 'system-evidence', label: 'System Evidence' },
            { id: 'claim-classification', label: 'Claim Classes' },
        ],
    },
    {
        path: '/security',
        label: 'Security',
        sections: [
            { id: 'overview', label: 'Overview' },
            { id: 'privacy-architecture', label: 'Privacy Architecture' },
            { id: 'routing-logic', label: 'Routing Logic' },
            { id: 'trust-boundary', label: 'Trust Boundary' },
            { id: 'truth-state', label: 'Truth State' },
        ],
    },
    {
        path: '/use-cases',
        label: 'Use Cases',
        sections: [
            { id: 'overview', label: 'Overview' },
            { id: 'who-benefits-first', label: 'Primary Users' },
            { id: 'why-cloud-defaults-fail', label: 'Cloud Defaults' },
            { id: 'read-path', label: 'Read Path' },
        ],
    },
    {
        path: '/pricing',
        label: 'Pricing',
        sections: [
            { id: 'overview', label: 'Overview' },
            { id: 'reservation-tracks', label: 'Tracks' },
            { id: 'what-reservation-means', label: 'What It Means' },
            { id: 'timeline', label: 'Timeline' },
            { id: 'reserve', label: 'Reserve' },
        ],
    },
    {
        path: '/faq',
        label: 'FAQ',
        sections: [
            { id: 'overview', label: 'Overview' },
            { id: 'product-faq', label: 'Product FAQ' },
            { id: 'company-faq', label: 'Company FAQ' },
        ],
    },
    {
        path: '/about',
        label: 'About',
        sections: [
            { id: 'overview', label: 'Overview' },
            { id: 'thesis', label: 'Thesis' },
            { id: 'team', label: 'Team' },
            { id: 'principles', label: 'Principles' },
            { id: 'current-stage', label: 'Current Stage' },
        ],
    },
    {
        path: '/contact',
        label: 'Contact',
        sections: [
            { id: 'overview', label: 'Overview' },
            { id: 'direct-contact', label: 'Direct Contact' },
            { id: 'early-access', label: 'Early Access' },
        ],
    },
];

function NavPageLink({
    page,
    currentPath,
}: {
    page: NavPage;
    currentPath: string;
}) {
    const isPageActive = currentPath === page.path;
    return (
        <div className="nav-page">
            <Link
                to={page.path}
                className={`nav-page__link${isPageActive ? ' is-active' : ''}`}
            >
                {page.label}
            </Link>
        </div>
    );
}

const Header = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [pastHero, setPastHero] = useState(false);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const location = useLocation();

    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname, location.hash]);

    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 20);
            setPastHero(window.scrollY > Math.max(window.innerHeight * 0.9, 640));
        };

        onScroll();
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <>
            <div className="nav-accent-bar" />

            <header className="nav-header" data-scrolled={scrolled ? 'true' : 'false'}>
                <div className="site-width">
                    <div className="nav-row">
                        <Link to="/" className="brand-link">
                            <img
                                src="/aven-logo-dark.png"
                                alt="Aven Studios logo"
                                className="brand-logo"
                                width={28}
                                height={28}
                            />
                            <span>Aven Studios</span>
                        </Link>

                        <nav className="nav-links" aria-label="Primary">
                            {NAV_PAGES.map((page) => (
                                <NavPageLink
                                    key={page.path}
                                    page={page}
                                    currentPath={location.pathname}
                                />
                            ))}
                        </nav>

                        <div className="nav-right">
                            <Link to="/contact" className="nav-reserve-btn">
                                Reserve
                                <span aria-hidden>{' >'}</span>
                            </Link>

                            <button
                                type="button"
                                onClick={() => setIsOpen((value) => !value)}
                                className="menu-toggle"
                                aria-label="Menu"
                                aria-expanded={isOpen}
                            >
                                <motion.span
                                    animate={{ rotate: isOpen ? 45 : 0, y: isOpen ? 6 : 0 }}
                                    className="menu-toggle__bar"
                                />
                                <motion.span
                                    animate={{ opacity: isOpen ? 0 : 1 }}
                                    className="menu-toggle__bar"
                                />
                                <motion.span
                                    animate={{ rotate: isOpen ? -45 : 0, y: isOpen ? -6 : 0 }}
                                    className="menu-toggle__bar"
                                />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mobile-nav-panel" data-open={isOpen ? 'true' : 'false'}>
                    <div className="site-width mobile-nav-inner">
                        {NAV_PAGES.map((page) => {
                            const isExpanded = !!expanded[page.path];
                            return (
                                <div
                                    key={`mobile-${page.path}`}
                                    className="mobile-nav-page"
                                    data-expanded={isExpanded ? 'true' : 'false'}
                                >
                                    <div className="mobile-nav-row">
                                        <Link
                                            to={page.path}
                                            className={`mobile-nav-link${
                                                location.pathname === page.path ? ' is-active' : ''
                                            }`}
                                        >
                                            {page.label}
                                        </Link>
                                        <button
                                            type="button"
                                            className="mobile-nav-chevron"
                                            aria-expanded={isExpanded}
                                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${page.label} sections`}
                                            onClick={() =>
                                                setExpanded((prev) => ({
                                                    ...prev,
                                                    [page.path]: !prev[page.path],
                                                }))
                                            }
                                        >
                                            <span aria-hidden="true">+</span>
                                        </button>
                                    </div>
                                    <div className="mobile-nav-subnav">
                                        {page.sections.map((section) => (
                                            <Link
                                                key={`mobile-${page.path}-${section.id}`}
                                                to={`${page.path}#${section.id}`}
                                                className={`mobile-nav-sublink${
                                                    location.pathname === page.path &&
                                                    location.hash === `#${section.id}`
                                                        ? ' is-active'
                                                        : ''
                                                }`}
                                            >
                                                {section.label}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </header>

            {pastHero ? (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                    <Link to="/contact" className="floating-cta">
                        Reserve position
                    </Link>
                </motion.div>
            ) : null}
        </>
    );
};

export default Header;
